import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { 
  Building2, 
  LogOut, 
  DollarSign,
  User,
  Home,
  Sparkles
} from 'lucide-react';
import { useLogout, useUser } from '../../hooks/api';
import { useNavigate } from 'react-router-dom';

const navigation = [
  {
    id: 'home',
    name: 'Home',
    icon: Home,
    href: '/',
  },
  {
    id: 'pricing',
    name: 'Pricing',
    icon: DollarSign,
    items: [
      { id: 'firms', name: 'Firms', icon: Building2, href: '/pricing/firms' },
    ]
  },
  {
    id: 'features',
    name: 'Features',
    icon: Sparkles,
    href: '/features',
  }
];

export function Sidebar() {
  const user = useUser();
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        navigate('/login', { replace: true });
      },
    });
  };

  return (
    <div className="flex h-full w-64 flex-col bg-sidebar border-r border-sidebar-border">
      {/* Header */}
      <div className="flex h-16 items-center justify-between px-6 border-b border-sidebar-border bg-sidebar-background">
        <h1 className="text-lg font-semibold text-sidebar-foreground">
          CQ Admin
        </h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
        {navigation.map((section) => {
          const isActive = section.href && location.pathname === section.href;

          return (
            <div key={section.id} className="space-y-1">
              {section.href ? (
                <Link
                  to={section.href}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                  )}
                >
                  <section.icon className="mr-3 h-4 w-4" />
                  {section.name}
                </Link>
              ) : (
                <>
                  <div className="flex items-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-sidebar-primary-foreground">
                    <section.icon className="mr-2 h-3.5 w-3.5" />
                    {section.name}
                  </div>
                  <div className="ml-6 space-y-0.5">
                    {section.items?.map((item) => {
                      const isItemActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.id}
                          to={item.href}
                          className={cn(
                            "flex items-center px-3 py-2 text-sm rounded-md transition-colors",
                            isItemActive
                              ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                              : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                          )}
                        >
                          <item.icon className="mr-3 h-4 w-4" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-sidebar-border p-4 bg-sidebar-background">
        <div className="flex items-center space-x-3 mb-3">
          {user?.avatar ? (
            <img 
              src={user.avatar} 
              alt={user.name}
              className="h-8 w-8 rounded-full object-cover border border-sidebar-border"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sidebar-accent border border-sidebar-border">
              <User className="h-4 w-4 text-sidebar-accent-foreground" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground truncate">
              {user?.name || 'Admin User'}
            </p>
            <p className="text-xs text-sidebar-primary-foreground truncate">
              {user?.email}
            </p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          onClick={handleLogout}
          disabled={logoutMutation.isPending}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  )
}
